<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn
            class="mr-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon> </v-btn
          >Generate Coupon
          <v-progress-circular
            v-if="isMasterLoading"
            class="ml-5"
            indeterminate
            color="primary"
          ></v-progress-circular>
        </v-card-title>
        <v-card-text>
          <h4 class="mb-3 grey--text text--darken-2">Coupon Code</h4>
          <v-row>
            <v-col sm="3">
              <v-text-field
                v-model="form.code"
                outlined
                label="Coupon Code"
                hint="Leave blank to auto generate"
                persistent-hint
                :error-messages="errors.code"
                @input="form.code = (form.code || '').toUpperCase()"
              ></v-text-field>
            </v-col>
            <v-col sm="3">
              <v-text-field
                v-model="form.code_prefix"
                outlined
                label="Prefix"
                hint="e.g. RIYA gives RIYAX7K2"
                persistent-hint
                :error-messages="errors.code_prefix"
              ></v-text-field>
            </v-col>
            <v-col sm="2">
              <v-btn
                class="mt-2"
                rounded
                color="secondary"
                :loading="isGenerating"
                @click="generateCode"
              >
                <v-icon left>mdi-refresh</v-icon> Generate
              </v-btn>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="form.title"
                outlined
                label="Title"
                hint="Internal name, e.g. Riya Instagram"
                :error-messages="errors.title"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>
          <h4 class="mb-3 grey--text text--darken-2">Discount</h4>
          <v-row>
            <v-col sm="3">
              <v-autocomplete
                outlined
                v-model="form.discount_type"
                :items="discountTypes"
                label="Discount Type *"
                :error-messages="errors.discount_type"
              ></v-autocomplete>
            </v-col>
            <v-col sm="3">
              <v-text-field
                v-model="form.discount_value"
                outlined
                type="number"
                :label="
                  form.discount_type == 'percent'
                    ? 'Discount (%) *'
                    : 'Discount (₹) *'
                "
                :error-messages="errors.discount_value"
              ></v-text-field>
            </v-col>
            <v-col sm="3" v-if="form.discount_type == 'percent'">
              <v-text-field
                v-model="form.max_discount_amount"
                outlined
                type="number"
                label="Max Discount (&#8377;)"
                hint="Cap on a percent coupon"
                :error-messages="errors.max_discount_amount"
              ></v-text-field>
            </v-col>
            <v-col sm="3">
              <v-text-field
                v-model="form.min_order_amount"
                outlined
                type="number"
                label="Min Order Amount (&#8377;)"
                :error-messages="errors.min_order_amount"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col sm="3">
              <v-autocomplete
                outlined
                v-model="form.applicable_on"
                :items="applicableOnItems"
                label="Applicable On *"
                :error-messages="errors.applicable_on"
              ></v-autocomplete>
            </v-col>
            <v-col sm="9">
              <v-autocomplete
                outlined
                multiple
                chips
                deletable-chips
                v-model="form.plan_ids"
                :items="planItems"
                label="Limit to Plans"
                hint="Leave empty to allow all plans"
                persistent-hint
                clearable
                :error-messages="errors.plan_ids"
              >
                <template v-slot:prepend-item>
                  <v-list-item ripple @mousedown.prevent @click="toggleSelectAllPlans">
                    <v-list-item-action>
                      <v-icon :color="form.plan_ids.length > 0 ? 'primary' : ''">
                        {{ selectAllPlansIcon }}
                      </v-icon>
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>Select All</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider class="mt-2"></v-divider>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>
          <h4 class="mb-3 grey--text text--darken-2">Usage Limit</h4>
          <v-row>
            <v-col sm="3">
              <v-text-field
                v-model="form.usage_limit"
                outlined
                type="number"
                label="Total Uses"
                hint="e.g. 3 = only first 3 users. Blank = unlimited"
                persistent-hint
                :error-messages="errors.usage_limit"
              ></v-text-field>
            </v-col>
            <v-col sm="3">
              <v-text-field
                v-model="form.usage_limit_per_user"
                outlined
                type="number"
                label="Uses Per User"
                :error-messages="errors.usage_limit_per_user"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>
          <h4 class="mb-3 grey--text text--darken-2">Validity</h4>
          <v-row>
            <v-col sm="3">
              <v-menu
                v-model="validFromMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.valid_from"
                    label="Valid From"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    hide-details="auto"
                    clearable
                    @click:clear="form.valid_from = null"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="form.valid_from"
                  @input="validFromMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col sm="3">
              <v-text-field
                v-model="form.validity_days"
                outlined
                type="number"
                label="Valid For (days)"
                hint="e.g. 1 or 3. End date is worked out from Valid From"
                persistent-hint
                :error-messages="errors.validity_days"
              ></v-text-field>
            </v-col>
            <v-col sm="3">
              <v-menu
                v-model="validTillMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.valid_till"
                    label="Valid Till"
                    hint="Or set an exact end date instead of days"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    hide-details="auto"
                    clearable
                    @click:clear="form.valid_till = null"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="form.valid_till"
                  @input="validTillMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>
          <h4 class="mb-3 grey--text text--darken-2">Influencer</h4>
          <v-row>
            <v-col sm="3">
              <v-switch
                v-model="form.is_influencer_coupon"
                label="Influencer Coupon"
              ></v-switch>
            </v-col>
            <template v-if="form.is_influencer_coupon">
              <v-col sm="3">
                <v-text-field
                  v-model="form.influencer_name"
                  outlined
                  label="Influencer Name"
                  :error-messages="errors.influencer_name"
                ></v-text-field>
              </v-col>
              <v-col sm="3">
                <v-text-field
                  v-model="form.influencer_phone"
                  outlined
                  label="Influencer Phone"
                  :error-messages="errors.influencer_phone"
                ></v-text-field>
              </v-col>
              <v-col sm="3">
                <v-autocomplete
                  outlined
                  v-model="form.influencer_platform"
                  :items="platformItems"
                  label="Platform"
                  :error-messages="errors.influencer_platform"
                ></v-autocomplete>
              </v-col>
            </template>
          </v-row>
          <v-row>
            <v-col sm="12">
              <v-textarea
                v-model="form.description"
                outlined
                rows="2"
                label="Description"
                :error-messages="errors.description"
              ></v-textarea>
            </v-col>
          </v-row>

          <v-divider class="my-5"> </v-divider>
          <v-btn v-can:edit="'coupons'"
            rounded
            color="primary"
            :loading="isLoading"
            :disabled="isLoading"
            @click="save"
          >
            Save Coupon
          </v-btn>
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>
</template>
<script>
import axios from "axios";
export default {
  name: "CouponCreatePage",
  data() {
    return {
      form: {
        code: "",
        code_prefix: "",
        title: "",
        description: "",
        discount_type: "percent",
        discount_value: "",
        max_discount_amount: "",
        min_order_amount: 0,
        applicable_on: "plan",
        plan_ids: [],
        usage_limit: "",
        usage_limit_per_user: 1,
        valid_from: "",
        valid_till: "",
        validity_days: "",
        is_influencer_coupon: false,
        influencer_name: "",
        influencer_phone: "",
        influencer_platform: "",
        is_active: true,
      },
      discountTypes: [
        { id: "percent", text: "PERCENT (%)", value: "percent" },
        { id: "fixed", text: "FLAT (₹)", value: "fixed" },
      ],
      applicableOnItems: [
        { id: "plan", text: "PLAN PURCHASE", value: "plan" },
        { id: "wallet", text: "WALLET TOP-UP", value: "wallet" },
        { id: "all", text: "BOTH", value: "all" },
      ],
      platformItems: [
        { id: "instagram", text: "INSTAGRAM", value: "instagram" },
        { id: "youtube", text: "YOUTUBE", value: "youtube" },
        { id: "facebook", text: "FACEBOOK", value: "facebook" },
        { id: "other", text: "OTHER", value: "other" },
      ],
      planItems: [],
      validFromMenu: false,
      validTillMenu: false,
      isLoading: false,
      isGenerating: false,
      isMasterLoading: false,
      isComputingValidity: false,
    };
  },
  computed: {
    selectAllPlansIcon() {
      if (!this.form.plan_ids.length) return "mdi-checkbox-blank-outline";
      if (this.form.plan_ids.length === this.planItems.length)
        return "mdi-close-box";
      return "mdi-minus-box";
    },
  },
  watch: {
    "form.valid_from"() {
      if (this.isComputingValidity) return;
      if (this.form.validity_days) {
        this.recalcValidTillFromDays();
      } else if (this.form.valid_till) {
        this.recalcDaysFromValidTill();
      }
    },
    "form.validity_days"() {
      if (this.isComputingValidity) return;
      this.recalcValidTillFromDays();
    },
    "form.valid_till"() {
      if (this.isComputingValidity) return;
      this.recalcDaysFromValidTill();
    },
  },
  mounted() {
    this.getPlans();
  },
  methods: {
    toggleSelectAllPlans() {
      this.$nextTick(() => {
        this.form.plan_ids =
          this.form.plan_ids.length === this.planItems.length
            ? []
            : this.planItems.map((plan) => plan.value);
      });
    },
    parseDateOnly(str) {
      const [y, m, d] = str.split("-").map(Number);
      return new Date(y, m - 1, d);
    },
    formatDateOnly(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },
    recalcValidTillFromDays() {
      if (!this.form.valid_from || !this.form.validity_days) return;
      const days = parseInt(this.form.validity_days, 10);
      if (isNaN(days)) return;
      const from = this.parseDateOnly(this.form.valid_from);
      from.setDate(from.getDate() + days);
      this.isComputingValidity = true;
      this.form.valid_till = this.formatDateOnly(from);
      this.$nextTick(() => {
        this.isComputingValidity = false;
      });
    },
    recalcDaysFromValidTill() {
      if (!this.form.valid_from || !this.form.valid_till) return;
      const from = this.parseDateOnly(this.form.valid_from);
      const till = this.parseDateOnly(this.form.valid_till);
      const diffDays = Math.round((till - from) / 86400000);
      this.isComputingValidity = true;
      this.form.validity_days = diffDays >= 0 ? diffDays : null;
      this.$nextTick(() => {
        this.isComputingValidity = false;
      });
    },
    async getPlans() {
      this.isMasterLoading = true;
      await axios
        .get(`plans`)
        .then((response) => {
          this.planItems = (response.data.data || []).map((plan) => ({
            id: plan.id,
            value: plan.id,
            text: `${plan.tier || "Plan"} - ${plan.no_of_dogs || 0} dog(s), ${
              plan.no_of_walks || 0
            } walks (₹${plan.final_amount || 0})`,
          }));
        })
        .catch((error) => {
          console.error(error);
        });
      this.isMasterLoading = false;
    },
    async generateCode() {
      this.isGenerating = true;
      await axios
        .get(`coupons/generate_code?code_prefix=${this.form.code_prefix || ""}`)
        .then((response) => {
          this.form.code = response.data.data.code;
        })
        .catch((error) => {
          console.error(error);
          this.isUnAuthorised();
        });
      this.isGenerating = false;
    },
    // Blank optional numbers must go as null, not "", or validation trips
    buildPayload() {
      const payload = Object.assign({}, this.form);
      [
        "max_discount_amount",
        "usage_limit",
        "validity_days",
        "valid_from",
        "valid_till",
        "code",
        "code_prefix",
      ].forEach((key) => {
        if (payload[key] === "" || payload[key] === undefined) {
          payload[key] = null;
        }
      });
      if (!payload.plan_ids || payload.plan_ids.length === 0) {
        payload.plan_ids = null;
      }
      return payload;
    },
    async save() {
      try {
        this.isLoading = true;
        await axios
          .post("/coupons", this.buildPayload())
          .then(async (response) => {
            await this.saveUserTimestamp(null, this.form);
            this.isLoading = false;
            this.isShowPopUP(
              "Coupon Generated",
              `Code: ${response.data.data.code}`,
              "success"
            );
            this.$router.push("/coupons");
          })
          .catch((error) => {
            this.isLoading = false;
            const data = error.response ? error.response.data : {};
            if (error.response && error.response.status === 401) {
              this.isUnAuthorised();
            } else {
              this.isShowPopUP(
                "Coupon not saved",
                typeof data.errors === "string"
                  ? data.errors
                  : data["sub-title"] || "Please check the form.",
                "error"
              );
            }
          });
      } catch (e) {
        console.log(e);
        this.isLoading = false;
      }
    },
  },
};
</script>
